import { CKSkeleton } from 'clearkit';
import { useEffect, useState } from 'react';

import { useStatusToast } from '~/components/unified/hooks';
import {
  BillingInfo,
  useAccountBillingInfoQuery,
  useAccountUpdateBillingInfoMutation,
} from '~/generated/graphql';

import { FormBillingDetails } from './FormBillingDetails';
import { FormPaymentInformation } from './FormPaymentInformation';
import { InvoiceList } from './InvoiceList';

export const BillingPaymentPage = () => {
  const { addSuccessToast, addErrorToast } = useStatusToast();
  const { data, refetch } = useAccountBillingInfoQuery();

  const [accountBillingInfo, setAccountBillingInfo] = useState<
    BillingInfo | undefined
  >(data?.accountBillingInfo);

  const [updateBillingInfo] = useAccountUpdateBillingInfoMutation({
    onCompleted: (data: any) => {
      if (data.errors?.length) {
        addErrorToast({
          description: 'There was a problem updating your settings. Try again.',
          heading: 'Billing Details Not Updated',
        });
      } else {
        addSuccessToast({
          description: 'Your settings have been updated.',
          heading: 'Billing Details Updated',
        });
      }
    },
  });

  const handleUpdateBilling = async (value: BillingInfo) => {
    updateBillingInfo({
      variables: {
        input: {
          billingEmail: value.billingEmail as string,
          name: value.billingName as string,
          address: value.address as string,
        },
      },
    });

    await setAccountBillingInfo(value);
  };

  const handleUpdatePayment = async (value: BillingInfo) => {
    await setAccountBillingInfo(value);

    addSuccessToast({
      description: 'Your card has been updated.',
      heading: 'Payment method updated',
    });
  };

  useEffect(() => {
    setAccountBillingInfo(data?.accountBillingInfo);
  }, [data]);

  return (
    <div className="space-y-8">
      {accountBillingInfo ? (
        <FormBillingDetails
          onChange={handleUpdateBilling}
          value={accountBillingInfo}
        />
      ) : (
        <CKSkeleton className="w-full h-[28rem]" isLoading />
      )}
      {accountBillingInfo ? (
        <FormPaymentInformation
          billingInfo={accountBillingInfo}
          handleSave={handleUpdatePayment}
          onChange={handleUpdatePayment}
          refetch={refetch}
        />
      ) : (
        <CKSkeleton className="w-full h-[20rem]" isLoading />
      )}
      <InvoiceList invoices={accountBillingInfo?.invoices || []} />
    </div>
  );
};
